import React, { useState } from 'react';
import RichTextEditor from './RichTextEditor';
import VideoUploader from './VideoUploader';
import DocumentUploader from './DocumentUploader';
import ContentBlockCard from './ContentBlockCard';
import { useApi } from '../../hooks/useApi';
import { useAppState } from '../../context/AppStateContext';
import { useQueryClient } from '@tanstack/react-query';
import {
    PlusIcon,
    PencilSquareIcon,
    VideoCameraIcon,
    DocumentTextIcon,
    XMarkIcon
} from '@heroicons/react/24/outline';
import ConfirmModal from '../common/ConfirmModal';

const ContentManager = ({ lesson, onClose, onSave }) => {
    const { post, patch, del } = useApi();
    const { showToast } = useAppState();
    const queryClient = useQueryClient();
    const [localLesson, setLocalLesson] = useState(lesson);
    const [contentBlocks, setContentBlocks] = useState(lesson.contents || []);
    const [activeEditor, setActiveEditor] = useState(null);
    const [editingBlock, setEditingBlock] = useState(null);
    const [textContent, setTextContent] = useState('');
    const [saving, setSaving] = useState(false);
    const [triggerSubmit, setTriggerSubmit] = useState(false);
    const [blockToDelete, setBlockToDelete] = useState(null);

    // Sync local state when lesson prop updates
    React.useEffect(() => {
        if (lesson) {
            setLocalLesson(lesson);
            if (lesson.contents || lesson.teacher_contents) {
                setContentBlocks(lesson.contents || lesson.teacher_contents);
            }
        }
    }, [lesson]);

    const handleAddContent = (type) => {
        setActiveEditor(type);
        setEditingBlock(null);
        setTriggerSubmit(false);
        if (type === 'text') {
            setTextContent('');
        }
    };

    const handleEditBlock = (block) => {
        setEditingBlock(block);
        setActiveEditor(block.content_type);
        if (block.content_type === 'text') {
            setTextContent(block.body || '');
        }
    };

    const handleSaveTextBlock = async () => {
        if (!textContent.trim()) {
            showToast('Please enter some content', 'error');
            return;
        }

        setSaving(true);
        try {
            const payload = {
                content_type: 'text',
                title: 'Notes',
                body: textContent,
                order: editingBlock ? editingBlock.order : contentBlocks.length + 1,
                is_published: editingBlock ? editingBlock.is_published : false,
            };

            if (!editingBlock) {
                payload.lesson = localLesson.lms_lesson_id || null;
                payload.sub_strand_id = localLesson.id;
            }

            let response;
            if (editingBlock && typeof editingBlock.id === 'number' && editingBlock.id < 1000000000) {
                response = await patch(`/api/lesson-contents/${editingBlock.id}/`, payload);
            } else {
                response = await post('/api/lesson-contents/', payload);
            }

            if (response.lesson && response.lesson !== localLesson.lms_lesson_id) {
                setLocalLesson(prev => ({ ...prev, lms_lesson_id: response.lesson }));
            }

            if (editingBlock) {
                setContentBlocks(contentBlocks.map(b => b.id === editingBlock.id ? response : b));
            } else {
                setContentBlocks([...contentBlocks, response]);
            }

            setActiveEditor(null);
            setEditingBlock(null);
            setTextContent('');
            showToast('Draft saved successfully!');
            // Force a background refresh of all course data to ensure persistence in UI
            queryClient.invalidateQueries({ queryKey: ['course'] });
            queryClient.invalidateQueries({ queryKey: ['course-detail'] });
        } catch (error) {
            console.error('Error saving text block:', error);
            showToast('Failed to save draft', 'error');
        } finally {
            setSaving(false);
        }
    };

    const handleSaveVideoBlock = async (data) => {
        setSaving(true);
        try {
            const formData = new FormData();
            if (!editingBlock) {
                formData.append('lesson', localLesson.lms_lesson_id || '');
                formData.append('sub_strand_id', localLesson.id);
            }
            formData.append('content_type', 'video');
            formData.append('title', data.title);
            formData.append('embed_url', data.embed_url || '');
            formData.append('order', editingBlock ? editingBlock.order : contentBlocks.length + 1);
            formData.append('is_published', editingBlock ? editingBlock.is_published : false);
            
            if (data.file) {
                formData.append('file', data.file);
            }

            let response;
            if (editingBlock && typeof editingBlock.id === 'number' && editingBlock.id < 1000000000) {
                response = await patch(`/api/lesson-contents/${editingBlock.id}/`, formData, {
                    headers: { 'Content-Type': 'multipart/form-data' }
                });
            } else {
                response = await post('/api/lesson-contents/', formData, {
                    headers: { 'Content-Type': 'multipart/form-data' }
                });
            }

            if (response.lesson && response.lesson !== localLesson.lms_lesson_id) {
                setLocalLesson(prev => ({ ...prev, lms_lesson_id: response.lesson }));
            }

            if (editingBlock) {
                setContentBlocks(contentBlocks.map(b => b.id === editingBlock.id ? response : b));
            } else {
                setContentBlocks([...contentBlocks, response]);
            }

            setActiveEditor(null);
            setEditingBlock(null);
            showToast('Video draft saved!');
        } catch (error) {
            console.error('Error saving video block:', error);
            showToast('Failed to save video draft', 'error');
        } finally {
            setSaving(false);
        }
    };

    const handleSaveDocumentBlock = async (data) => {
        setSaving(true);
        try {
            const payload = {
                content_type: 'document',
                title: data.title,
                resource_url: data.resource_url,
                order: editingBlock ? editingBlock.order : contentBlocks.length + 1,
                is_published: editingBlock ? editingBlock.is_published : false,
            };

            if (!editingBlock) {
                payload.lesson = localLesson.lms_lesson_id || null;
                payload.sub_strand_id = localLesson.id;
            }

            let response;
            if (editingBlock && typeof editingBlock.id === 'number' && editingBlock.id < 1000000000) {
                response = await patch(`/api/lesson-contents/${editingBlock.id}/`, payload);
            } else {
                response = await post('/api/lesson-contents/', payload);
            }

            if (response.lesson && response.lesson !== localLesson.lms_lesson_id) {
                setLocalLesson(prev => ({ ...prev, lms_lesson_id: response.lesson }));
            }

            if (editingBlock) {
                setContentBlocks(contentBlocks.map(b => b.id === editingBlock.id ? response : b));
            } else {
                setContentBlocks([...contentBlocks, response]);
            }

            setActiveEditor(null);
            setEditingBlock(null);
            setTriggerSubmit(false);
            showToast('Document draft saved!');
            // DO NOT call onSave() here
        } catch (error) {
            console.error('Error saving document block:', error);
            showToast('Failed to save document draft', 'error');
        } finally {
            setSaving(false);
        }
    };

    const handleDeleteBlock = async (blockId) => {
        if (typeof blockId === 'number' && blockId < 1000000000) {
            try {
                await del(`/api/lesson-contents/${blockId}/`);
                setContentBlocks(contentBlocks.filter(b => b.id !== blockId));
                showToast('Block deleted successfully');
                if (onSave) onSave();
            } catch (error) {
                console.error('Error deleting block:', error);
                showToast('Failed to delete block', 'error');
            }
        } else {
            setContentBlocks(contentBlocks.filter(b => b.id !== blockId));
        }
    };

    const handleMoveBlock = async (index, direction) => {
        const newBlocks = [...contentBlocks];
        const targetIndex = direction === 'up' ? index - 1 : index + 1;

        if (targetIndex < 0 || targetIndex >= newBlocks.length) return;

        [newBlocks[index], newBlocks[targetIndex]] = [newBlocks[targetIndex], newBlocks[index]];

        // Calculate explicit orders
        const orders = newBlocks.map((block, idx) => ({
            id: block.id,
            order: idx + 1
        })).filter(item => typeof item.id === 'number' && item.id < 1000000000);

        setContentBlocks([...newBlocks]);

        try {
            setSaving(true);
            // Use BATCH reorder endpoint to prevent database locking (N+1 fix)
            // Corrected URL to /api/teacher/ per core/urls.py
            await post('/api/teacher/lessons/content/reorder/', { orders });
            showToast('Order updated');
            if (onSave) onSave();
        } catch (error) {
            console.error('Error updating block order:', error);
            showToast('Failed to sync order', 'error');
        } finally {
            setSaving(false);
        }
    };

    const handleSaveAll = async () => {
        if (!contentBlocks.length) {
            onClose();
            return;
        }

        try {
            setSaving(true);
            const ids = contentBlocks
                .map(b => b.id)
                .filter(id => typeof id === 'number' && id < 1000000000);

            // Use BATCH publish endpoint (N+1 fix)
            // Corrected URL to /api/teacher/ per core/urls.py
            await post('/api/teacher/lessons/content/publish/', { ids });

            showToast('Content published successfully!');
            if (onSave) onSave();
            onClose();
        } catch (error) {
            console.error('Error publishing content:', error);
            showToast('Failed to publish content.', 'error');
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center z-[300] p-4 animate-in fade-in duration-200">
            <div className="bg-white rounded-[2.5rem] shadow-2xl max-w-5xl w-full max-h-[95vh] overflow-y-auto custom-scrollbar border border-slate-100 animate-in zoom-in-95 duration-200">
                <div className="px-10 py-8 bg-white border-b border-slate-100 flex items-center justify-between">
                    <div>
                        <h2 className="text-2xl font-black text-[#18216D] uppercase tracking-tight">Manage Content</h2>
                        <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mt-1">
                            <span className="text-[#FFC425] mr-2">/</span> {localLesson.name || localLesson.title}
                        </p>
                    </div>
                    <button
                        onClick={onClose}
                        className="p-2 hover:bg-slate-50 rounded-full transition-colors text-slate-300 hover:text-red-500"
                    >
                        <XMarkIcon className="w-6 h-6 stroke-[2.5]" />
                    </button>
                </div>

                <div className="p-10 pb-16 space-y-10">
                    {!activeEditor && (
                        <div className="bg-[#18216D]/5 rounded-2xl p-2 flex flex-col md:flex-row items-center justify-between border border-[#18216D]/10">
                            <div className="flex items-center gap-3 px-4 py-2">
                                <PlusIcon className="w-4 h-4 text-[#18216D]" />
                                <div>
                                    <p className="text-[10px] font-black text-[#18216D] uppercase tracking-widest leading-none">Swift-Add Toolbar</p>
                                    <p className="text-[8px] font-bold text-slate-400 uppercase tracking-tighter mt-1">Direct sub-strand entry</p>
                                </div>
                            </div>
                            <div className="flex items-center gap-2 p-1">
                                <button onClick={() => handleAddContent('text')} className="flex items-center gap-2 px-5 py-2.5 bg-white border border-slate-100 rounded-[1rem] text-[10px] font-black uppercase tracking-widest text-[#18216D] hover:bg-slate-50 transition-all shadow-sm group">
                                    <PencilSquareIcon className="w-4 h-4 text-[#18216D]/40 group-hover:text-[#18216D]" /> Rich Text
                                </button>
                                <button onClick={() => handleAddContent('video')} className="flex items-center gap-2 px-5 py-2.5 bg-white border border-slate-100 rounded-[1rem] text-[10px] font-black uppercase tracking-widest text-[#18216D] hover:bg-amber-50/30 transition-all shadow-sm group">
                                    <VideoCameraIcon className="w-4 h-4 text-[#FFC425]" /> Video
                                </button>
                                <button onClick={() => handleAddContent('document')} className="flex items-center gap-2 px-5 py-2.5 bg-white border border-slate-100 rounded-[1rem] text-[10px] font-black uppercase tracking-widest text-[#18216D] hover:bg-emerald-50/30 transition-all shadow-sm group">
                                    <DocumentTextIcon className="w-4 h-4 text-emerald-500" /> Document
                                </button>
                            </div>
                        </div>
                    )}

                    {activeEditor && (
                        <div className="animate-in fade-in zoom-in-95 duration-500">
                            {activeEditor === 'text' && (
                                <div className="bg-slate-50/50 rounded-[2.5rem] p-10 border border-slate-100">
                                    <RichTextEditor
                                        value={textContent}
                                        onChange={setTextContent}
                                        placeholder="Start typing your lesson content..."
                                    />
                                </div>
                            )}
                            {activeEditor === 'video' && (
                                <VideoUploader
                                    onSave={handleSaveVideoBlock}
                                    onCancel={() => { setActiveEditor(null); setEditingBlock(null); }}
                                    triggerSubmit={triggerSubmit}
                                    initialData={editingBlock}
                                />
                            )}
                            {activeEditor === 'document' && (
                                <DocumentUploader
                                    onSave={handleSaveDocumentBlock}
                                    onCancel={() => { setActiveEditor(null); setEditingBlock(null); }}
                                    triggerSubmit={triggerSubmit}
                                    initialData={editingBlock}
                                />
                            )}
                        </div>
                    )}

                    {!activeEditor && contentBlocks.length > 0 && (
                        <div className="space-y-4">
                            <h3 className="text-lg font-semibold text-gray-900 uppercase tracking-tight">Content Blocks</h3>
                            {contentBlocks.map((block, index) => (
                                <ContentBlockCard
                                    key={block.id || index}
                                    block={block}
                                    onEdit={() => handleEditBlock(block)}
                                    onDelete={() => setBlockToDelete(block.id || null)}
                                    onMoveUp={() => handleMoveBlock(index, 'up')}
                                    onMoveDown={() => handleMoveBlock(index, 'down')}
                                    isFirst={index === 0}
                                    isLast={index === contentBlocks.length - 1}
                                />
                            ))}
                        </div>
                    )}
                </div>

                <div className="px-10 py-8 bg-slate-50 border-t border-slate-100 flex items-center justify-end space-x-4">
                    <button
                        onClick={activeEditor ? () => setActiveEditor(null) : onClose}
                        className="px-8 py-3 bg-white border border-slate-200 text-[#18216D]/60 rounded-2xl font-black uppercase text-[10px] tracking-widest transition-all"
                    >
                        {activeEditor ? 'Discard Draft' : 'Close'}
                    </button>
                    {!activeEditor && (
                        <button
                            onClick={handleSaveAll}
                            disabled={saving || !contentBlocks.length}
                            className="px-8 py-3 bg-amber-500 text-white rounded-2xl hover:bg-amber-600 transition-all font-black uppercase text-xs tracking-widest shadow-xl shadow-amber-900/20 disabled:opacity-50"
                        >
                            {saving ? 'Publishing...' : 'PUBLISH'}
                        </button>
                    )}
                    {activeEditor && (
                        <button
                            onClick={
                                activeEditor === 'text' ? handleSaveTextBlock :
                                    () => setTriggerSubmit(true)
                            }
                            disabled={saving}
                            className="px-10 py-3 bg-[#18216D] text-[#FFC425] rounded-2xl font-black uppercase text-[10px] tracking-widest hover:scale-[1.02] border border-[#18216D] transition-all shadow-lg"
                        >
                            {saving ? 'Saving...' : (editingBlock ? 'Confirm Edit' : 'Confirm & Add Block')}
                        </button>
                    )}
                </div>
            </div>

            <ConfirmModal
                isOpen={!!blockToDelete}
                onClose={() => setBlockToDelete(null)}
                onConfirm={() => {
                    handleDeleteBlock(blockToDelete);
                    setBlockToDelete(null);
                }}
                title="Delete Content Block"
                message="Are you sure you want to delete this content block? This action cannot be undone."
                confirmText="Delete Block"
            />
        </div>
    );
};

export default ContentManager;
